import PropTypes from 'prop-types';
import {
  Box, Typography, Switch, Avatar, Chip, CircularProgress,
  TextField, Button, Select, MenuItem, FormControl, InputLabel, Stack, Alert,
} from '@mui/material';
import { WhatsApp as WhatsAppIcon, FiberManualRecord } from '@mui/icons-material';
import StandardCard from '../../components/ui/StandardCard';
import SettingsSection from '../../components/ui/SettingsSection';
import SettingRow from '../../components/ui/SettingRow';
import FormActionBar from '../../components/ui/FormActionBar';
import { brandColors } from '../../constants/brandColors';

function clampHour(v, fallback) {
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(24, Math.max(0, n));
}

/**
 * WhatsAppSettingsCard — estado ATIVADO: cabeçalho de status + seções de
 * configuração + barra de salvar. Renderizado apenas quando o WhatsApp está
 * habilitado (o estado desativado é o WhatsAppHero).
 */
export default function WhatsAppSettingsCard({
  savingSettings,
  togglingEnabled,
  form,
  updateField,
  handleSave,
  onDisable,
  models,
}) {
  const bStart = clampHour(form.reminderBusinessStart, 8);
  const bEnd = clampHour(form.reminderBusinessEnd, 18);

  return (
    <StandardCard accentColor={brandColors.whatsapp.main} fillHeight={false}>
      {/* Cabeçalho de status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2, mb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Avatar
          variant="rounded"
          sx={{ bgcolor: 'rgba(37,211,102,0.12)', color: brandColors.whatsapp.main, width: 40, height: 40 }}
        >
          <WhatsAppIcon fontSize="small" />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ lineHeight: 1.2 }}>WhatsApp</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FiberManualRecord sx={{ fontSize: 9, color: 'success.main' }} />
            <Typography variant="caption" color="text.secondary">Ativo</Typography>
          </Box>
        </Box>
        {togglingEnabled ? (
          <CircularProgress size={20} />
        ) : (
          <Chip
            label="Desativar"
            size="small"
            variant="outlined"
            onClick={onDisable}
            sx={{ cursor: 'pointer' }}
          />
        )}
      </Box>

      {/* Mensagem ao cliente */}
      <SettingsSection title="Mensagem ao cliente" description="Escolha o modelo e adicione orientações. Veja o resultado na pré-visualização.">
        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
          <InputLabel id="wa-style-label">Modelo da mensagem</InputLabel>
          <Select
            labelId="wa-style-label"
            label="Modelo da mensagem"
            value={form.templateStyle}
            onChange={(e) => updateField('templateStyle', Number(e.target.value))}
          >
            {(models.length ? models : [{ id: 1, name: 'Modelo 1' }, { id: 2, name: 'Modelo 2' }]).map((m) => (
              <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Orientações ao cliente (opcional)"
          value={form.messageNotes}
          onChange={(e) => updateField('messageNotes', e.target.value)}
          multiline
          minRows={2}
          maxRows={6}
          fullWidth
          inputProps={{ maxLength: 1000 }}
          helperText="Aparece nas mensagens ao cliente. Ex.: vir em jejum, trazer documento, chegar 10min antes."
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', lg: 'none' }, mt: 1.5 }}>
          A pré-visualização aparece abaixo ↓
        </Typography>
      </SettingsSection>

      {/* Avisar na criação */}
      <SettingsSection title="Avisar o cliente ao criar o agendamento">
        <SettingRow
          label="Quando o agendamento vem do site público"
          control={
            <Switch checked={form.notifyOnPublicCreate} onChange={(e) => updateField('notifyOnPublicCreate', e.target.checked)} color="success" />
          }
        />
        <SettingRow
          label="Quando você cria o agendamento aqui no painel"
          control={
            <Switch checked={form.notifyOnPrivateCreate} onChange={(e) => updateField('notifyOnPrivateCreate', e.target.checked)} color="success" />
          }
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Se desligar, não enviamos a mensagem de criação — apenas o lembrete (se ativo).
          Ao criar um agendamento no painel você ainda pode forçar o envio caso a caso.
        </Typography>
      </SettingsSection>

      {/* Lembrete */}
      <SettingsSection
        title="Lembrete antes do agendamento"
        description="Consome cota de mensagens e traz os botões Confirmar / Cancelar."
        action={
          <Switch checked={form.reminderEnabled} onChange={(e) => updateField('reminderEnabled', e.target.checked)} color="success" />
        }
      >
        {form.reminderEnabled && (
          <Stack spacing={2}>
            <SettingRow
              label="Horas antes"
              control={
                <TextField
                  type="number" size="small"
                  value={form.reminderHoursBefore}
                  onChange={(e) => updateField('reminderHoursBefore', e.target.value)}
                  inputProps={{ min: 1, max: 168 }}
                  sx={{ width: 100 }}
                />
              }
            />
            <SettingRow
              label="Respeitar horário comercial"
              control={
                <Switch checked={form.reminderRespectBusinessHours} onChange={(e) => updateField('reminderRespectBusinessHours', e.target.checked)} color="success" />
              }
            />
            {form.reminderRespectBusinessHours && (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', pl: { sm: 2 } }}>
                <TextField
                  label="Início" type="number" size="small"
                  value={form.reminderBusinessStart}
                  onChange={(e) => updateField('reminderBusinessStart', e.target.value)}
                  inputProps={{ min: 0, max: 24 }}
                  sx={{ width: 90 }}
                />
                <Typography variant="body2" color="text.secondary">às</Typography>
                <TextField
                  label="Fim" type="number" size="small"
                  value={form.reminderBusinessEnd}
                  onChange={(e) => updateField('reminderBusinessEnd', e.target.value)}
                  inputProps={{ min: 0, max: 24 }}
                  sx={{ width: 90 }}
                />
              </Stack>
            )}
            <Alert severity="info">
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Se o horário do lembrete cair fora do expediente, ele é deslocado para dentro dele.
                Exemplos (expediente {bStart}h–{bEnd}h):
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <li>Agendamento às 14h, lembrete 3h antes → 11h → enviado <b>11h do mesmo dia</b>.</li>
                <li>Agendamento ao meio-dia, lembrete 12h antes → 00h → enviado <b>{bEnd}h do dia anterior</b>.</li>
                <li>Agendamento às 20h, lembrete 1h antes → 19h → enviado <b>{bEnd}h do mesmo dia</b>.</li>
              </Box>
            </Alert>
          </Stack>
        )}
      </SettingsSection>

      {/* Notificações pro dono */}
      <SettingsSection
        title="Notificações pra você (dono)"
        description="Receba no seu WhatsApp quando algo acontecer. Cada aviso consome 1 crédito."
        action={
          <Switch checked={form.ownerNotifyEnabled} onChange={(e) => updateField('ownerNotifyEnabled', e.target.checked)} color="success" />
        }
      >
        {form.ownerNotifyEnabled && (
          <Stack spacing={2}>
            <TextField
              label="Meu WhatsApp (com DDD)"
              value={form.ownerPhone}
              onChange={(e) => updateField('ownerPhone', e.target.value)}
              placeholder="(31) 99999-9999"
              inputProps={{ inputMode: 'tel', maxLength: 20 }}
              sx={{ maxWidth: 280 }}
              fullWidth
            />
            <SettingRow
              label="Novo agendamento"
              control={<Switch checked={form.ownerNotifyOnCreate} onChange={(e) => updateField('ownerNotifyOnCreate', e.target.checked)} color="success" />}
            />
            <SettingRow
              label="Cliente confirmou presença"
              control={<Switch checked={form.ownerNotifyOnConfirm} onChange={(e) => updateField('ownerNotifyOnConfirm', e.target.checked)} color="success" />}
            />
            <SettingRow
              label="Cliente cancelou/desistiu"
              control={<Switch checked={form.ownerNotifyOnCancel} onChange={(e) => updateField('ownerNotifyOnCancel', e.target.checked)} color="success" />}
            />
          </Stack>
        )}
      </SettingsSection>

      <FormActionBar>
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          disabled={savingSettings}
          startIcon={savingSettings ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Salvar configurações
        </Button>
      </FormActionBar>
    </StandardCard>
  );
}

WhatsAppSettingsCard.propTypes = {
  savingSettings: PropTypes.bool.isRequired,
  togglingEnabled: PropTypes.bool,
  form: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  onDisable: PropTypes.func.isRequired,
  models: PropTypes.array.isRequired,
};
